import React, { useState } from 'react';
import { Text } from 'office-ui-fabric-react/lib/Text';
import get from 'lodash/get';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { Dropdown } from 'office-ui-fabric-react/lib/Dropdown';
import { MessageBar, MessageBarType } from 'office-ui-fabric-react';
import SearchBar from '../../components/SearchBar';
import Module from '../../components/Module';
import ModuleContentList from '../../components/ModuleContentList';
import AdjustmentsList from '../../components/AdjustmentsList';
import Pagination from '../../components/Pagination';
import { fetchAdjustments } from '../../actions/adjustments';
import styles from './AdjustmentListTab.style';
import theme from '../../theme.style';
import useAPI from '../../hooks/useAPI';
import { AdjustmentStatus } from '../../utils/adjustmentUtils';

const AdjustmentListTab = ({ hiddenColumns, contractId, perLineItem }) => {
  const [resetPagination, setResetPagination] = useState(false);
  const [filterState, setFilterState] = useState(() => {
    return {
      contractId,
      duration: contractId ? '90-Days' : '',
      perLineItem,
    };
  });
  const [currentPageVal, setCurrentPageVal] = useState(0);
  const pageLimit = 20;
  const adjustments = useSelector(state =>
    get(state, 'pagination.adjustments.results', []).map(_id =>
      get(state, `entities.adjustments.${_id}`),
    ),
  );

  const totalCount = useSelector(state => get(state, 'pagination.adjustments.totalCount', 0));
  const adjustmentsList = useAPI(() => fetchAdjustments(filterState), [filterState]);

  const statusFilterOptions = [
    { key: 0, text: 'All Adjustments', status: '' },
    { key: 1, text: 'Draft', status: AdjustmentStatus.Draft },
    { key: 2, text: 'Approval Requested', status: AdjustmentStatus.ApprovalRequested },
    { key: 3, text: 'Approved', status: AdjustmentStatus.Approved },
    { key: 4, text: 'Rejected', status: AdjustmentStatus.Rejected },
    { key: 5, text: 'Reverse Requested', status: AdjustmentStatus.ReversalApprovalRequested },
    { key: 6, text: 'Reversed', status: AdjustmentStatus.ReversalApproved },
    { key: 7, text: 'Reverse Rejected', status: AdjustmentStatus.ReversalRejected },
    { key: 8, text: 'Auto Reversed', status: AdjustmentStatus.AutoReversed },
  ];

  const durationFilterOptions = [
    { key: 0, text: '90 Days', duration: '90-Days' },
    { key: 1, text: 'Year to Date', duration: 'Year-to-Date' },
    { key: 2, text: 'Contract to Date', duration: '' },
  ];

  const updateAdjustmentFilters = adjustmentFilters => {
    setFilterState(adjustmentFilters);
  };

  const updateSearchFilter = async sValue => {
    const newFilterState = {
      ...filterState,
      query: sValue,
      start: null,
    };
    updateAdjustmentFilters(newFilterState);
  };

  const handleSearch = sValue => {
    setResetPagination(true);
    updateSearchFilter(sValue);
  };

  const handleClear = () => {
    setResetPagination(true);
    updateSearchFilter();
  };

  const onStatusChange = async (event, option) => {
    const newFilterState = {
      ...filterState,
      status: option.status,
      start: null,
    };
    setResetPagination(true);
    updateAdjustmentFilters(newFilterState);
  };

  const onDurationChange = async (event, option) => {
    const newFilterState = {
      ...filterState,
      duration: option.duration,
      start: null,
    };
    setResetPagination(true);
    updateAdjustmentFilters(newFilterState);
  };

  const handlePageChange = paginationData => {
    setResetPagination(false);
    setCurrentPageVal(paginationData.currentPageIndex);
    const newFilterState = {
      ...filterState,
      start: paginationData.currentPageIndex * paginationData.pageLimit,
      count: paginationData.pageLimit,
    };

    updateAdjustmentFilters(newFilterState);
  };

  return (
    <>
      <div className={styles.searchSection}>
        <SearchBar
          className={styles.search}
          placeholder="Search adjustments"
          ariaLabel="Search"
          data-testid="search-input"
          onChange={handleSearch}
          onClear={handleClear} // when you click X
        />
        <Dropdown
          data-testid="status-dropdown"
          ariaLabel="StatusFilter"
          placeholder={contractId ? '90 Days' : 'All Adjustments'}
          label={contractId ? 'Duration' : 'Status'}
          options={contractId ? durationFilterOptions : statusFilterOptions}
          className={styles.filter}
          styles={{
            title: { backgroundColor: theme.palette.neutralWhite },
            dropdown: { fontWeight: 'normal' },
            dropdownItem: { fontWeight: 'normal' },
          }}
          onChange={contractId ? onDurationChange : onStatusChange}
        />
      </div>

      {(() => {
        if (adjustmentsList.isLoading) {
          return (
            <Module>
              <ModuleContentList>
                <AdjustmentsList
                  items={[]}
                  shimmered
                  enableShimmer
                  noBorderBottom
                  data-testid="adjustment-list-items-shimmer"
                  hiddenColumns={hiddenColumns}
                  perLineItem={perLineItem}
                />
              </ModuleContentList>
            </Module>
          );
        }

        if (adjustmentsList.error) {
          return (
            <MessageBar
              isMultiline={false}
              messageBarType={MessageBarType.error}
              messageBarIconProps={{ iconName: 'Info' }}
            >
              Something went wrong while loading adjustments. Please try refreshing the page.
            </MessageBar>
          );
        }

        if (adjustments.length === 0) {
          return (
            <div className={styles.resultsMessage}>
              {!contractId && (
                <Text as="h4" block>
                  No Results
                </Text>
              )}

              <Text as="p" block className={styles.noResultsSentence}>
                {contractId
                  ? 'No adjustments found for this contract. Try updating duration or search criteria.'
                  : 'Try searching by Adjustment Name, Adjustment Type, Contract Id, or Partner Name'}
              </Text>
            </div>
          );
        }

        return (
          <>
            <div className={styles.resultsMessage}>
              <Text as="h4" block>
                {totalCount} Result(s)
              </Text>
            </div>

            <Module>
              <ModuleContentList>
                <AdjustmentsList
                  items={adjustments}
                  shimmered
                  enableShimmer={false}
                  noBorderBottom
                  data-testid="adjustment-list-items"
                  hiddenColumns={hiddenColumns}
                  perLineItem={perLineItem}
                />
              </ModuleContentList>
              {((contractId && totalCount && totalCount > pageLimit) || !contractId) && (
                <Pagination
                  totalRecords={totalCount}
                  pageLimit={pageLimit}
                  handlePageChange={handlePageChange}
                  resetPagination={resetPagination}
                  currentPageVal={currentPageVal}
                  className={styles.pagination}
                />
              )}
            </Module>
          </>
        );
      })()}
    </>
  );
};

AdjustmentListTab.propTypes = {
  hiddenColumns: PropTypes.arrayOf(PropTypes.string),
  contractId: PropTypes.string,
  perLineItem: PropTypes.bool,
};

AdjustmentListTab.defaultProps = {
  hiddenColumns: [],
  contractId: undefined,
  perLineItem: false,
};

export default AdjustmentListTab;
